import React from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Database,
  LayoutGrid,
  MousePointerClick,
  CheckCircle2,
  Layers,
  Eye,
  Info,
  Users,
  BarChart3,
  FileText,
  Search,
  ArrowRight,
} from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import NewsletterForm from "@/components/NewsletterForm";

const STEP_ICONS = [Database, LayoutGrid, MousePointerClick, CheckCircle2];
const ADV_ICONS = [Layers, Eye, Info, Users];

export default function Home() {
  const { t } = useLang();

  const heroBullets = t("hero.bullets");
  const aboutBullets = t("about.bullets");
  const howSteps = t("how.steps");
  const visionBullets = t("vision.bullets");
  const advItems = t("advantages.items");
  const clarityBullets = t("clarity.bullets");
  const simpleBullets = t("simple.bullets");
  const structuredBullets = t("structured.bullets");
  const stepsBullets = t("steps.bullets");
  const organizeBullets = t("organize.bullets");
  const faqItems = t("faq.items");

  return (
    <main data-testid="page-home">
      {/* HERO */}
      <section className="relative bg-slate-50 border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-6">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-teal-700">
              {t("hero.eyebrow")}
            </p>
            <h1 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
              {t("hero.title")}
            </h1>
            <p className="mt-4 text-lg sm:text-xl text-slate-700 font-medium">
              {t("hero.subtitle")}
            </p>
            <ul className="mt-6 space-y-3 text-base text-slate-700">
              {heroBullets.map((b, i) => (
                <li key={i} className="flex gap-3">
                  <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
                  <span className="leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="#newsletter"
                data-testid="hero-cta-button"
                className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-6 py-3 text-sm font-semibold text-white hover:bg-slate-800"
              >
                {t("hero.primaryCta")}
                <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href="#how"
                data-testid="hero-secondary-cta"
                className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-6 py-3 text-sm font-semibold text-slate-800 hover:bg-slate-100"
              >
                {t("hero.secondaryCta")}
              </a>
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="relative overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
              <img
                src="/images/dashboard.jpg"
                alt={t("hero.imageAlt")}
                className="h-full w-full object-cover aspect-[4/3]"
                loading="eager"
                data-testid="hero-image"
              />
            </div>
            <div className="mt-6 grid grid-cols-3 gap-3 text-center">
              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <p className="font-heading text-xl font-bold text-slate-900">
                  {t("hero.stat1.value")}
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  {t("hero.stat1.label")}
                </p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <p className="font-heading text-xl font-bold text-slate-900">
                  {t("hero.stat2.value")}
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  {t("hero.stat2.label")}
                </p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <p className="font-heading text-xl font-bold text-slate-900">
                  {t("hero.stat3.value")}
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  {t("hero.stat3.label")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT SERVICE */}
      <section id="service" className="bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <div className="overflow-hidden rounded-xl border border-slate-200">
              <img
                src="/images/meeting.jpg"
                alt={t("about.imageAlt")}
                className="h-full w-full object-cover aspect-[4/3]"
                loading="lazy"
              />
            </div>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-teal-700">
              {t("about.eyebrow")}
            </p>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              {t("about.title")}
            </h2>
            <p className="mt-3 text-lg text-slate-700 font-medium">
              {t("about.subtitle")}
            </p>
            <ul className="mt-6 space-y-3 text-base text-slate-700">
              {aboutBullets.map((b, i) => (
                <li key={i} className="flex gap-3">
                  <FileText className="mt-1 h-4 w-4 shrink-0 text-teal-600" />
                  <span className="leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="bg-slate-900 text-slate-100">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-teal-400">
              {t("how.eyebrow")}
            </p>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tight text-white">
              {t("how.title")}
            </h2>
            <p className="mt-3 text-lg text-slate-300">{t("how.subtitle")}</p>
          </div>
          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {howSteps.map((s, i) => {
              const Icon = STEP_ICONS[i] || Database;
              return (
                <div
                  key={i}
                  data-testid={`how-step-${i + 1}`}
                  className="rounded-xl border border-slate-800 bg-slate-950 p-6"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs text-teal-400">
                      {s.number}
                    </span>
                    <Icon className="h-5 w-5 text-teal-400" />
                  </div>
                  <h3 className="mt-4 font-heading text-lg font-semibold text-white">
                    {s.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-300">
                    {s.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* VISION */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-6">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-teal-700">
              {t("vision.eyebrow")}
            </p>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              {t("vision.title")}
            </h2>
            <p className="mt-3 text-lg text-slate-700 font-medium">
              {t("vision.subtitle")}
            </p>
            <ul className="mt-6 space-y-3 text-base text-slate-700">
              {visionBullets.map((b, i) => (
                <li key={i} className="flex gap-3">
                  <Eye className="mt-1 h-4 w-4 shrink-0 text-teal-600" />
                  <span className="leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-6">
            <div className="overflow-hidden rounded-xl border border-slate-200">
              <img
                src="/images/monitoring.jpg"
                alt={t("vision.imageAlt")}
                className="h-full w-full object-cover aspect-[4/3]"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ADVANTAGES */}
      <section id="advantages" className="bg-slate-50 border-y border-slate-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-teal-700">
              {t("advantages.eyebrow")}
            </p>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              {t("advantages.title")}
            </h2>
            <p className="mt-3 text-lg text-slate-700">
              {t("advantages.subtitle")}
            </p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {advItems.map((it, i) => {
              const Icon = ADV_ICONS[i] || Layers;
              return (
                <div
                  key={i}
                  data-testid={`advantage-item-${i + 1}`}
                  className="rounded-xl border border-slate-200 bg-white p-6"
                >
                  <div className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-teal-600 text-white">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-5 font-heading text-lg font-semibold text-slate-900">
                    {it.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-700">
                    {it.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CLARITY / SIMPLE row */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-teal-700">
              {t("clarity.eyebrow")}
            </p>
            <h2 className="mt-3 font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
              {t("clarity.title")}
            </h2>
            <p className="mt-3 text-base text-slate-700 font-medium">
              {t("clarity.subtitle")}
            </p>
            <ul className="mt-5 space-y-3 text-sm text-slate-700">
              {clarityBullets.map((b, i) => (
                <li key={i} className="flex gap-3">
                  <BarChart3 className="mt-0.5 h-4 w-4 shrink-0 text-teal-600" />
                  <span className="leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-teal-700">
              {t("simple.eyebrow")}
            </p>
            <h2 className="mt-3 font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
              {t("simple.title")}
            </h2>
            <p className="mt-3 text-base text-slate-700 font-medium">
              {t("simple.subtitle")}
            </p>
            <ul className="mt-5 space-y-3 text-sm text-slate-700">
              {simpleBullets.map((b, i) => (
                <li key={i} className="flex gap-3">
                  <Search className="mt-0.5 h-4 w-4 shrink-0 text-teal-600" />
                  <span className="leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* SIMPLE IMAGE ROW */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-6">
            <div className="overflow-hidden rounded-xl border border-slate-200">
              <img
                src="/images/executives.jpg"
                alt={t("simple.imageAlt")}
                className="h-full w-full object-cover aspect-[4/3]"
                loading="lazy"
              />
            </div>
          </div>
          <div className="lg:col-span-6">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-teal-700">
              {t("structured.eyebrow")}
            </p>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              {t("structured.title")}
            </h2>
            <p className="mt-3 text-lg text-slate-700 font-medium">
              {t("structured.subtitle")}
            </p>
            <ul className="mt-6 space-y-3 text-base text-slate-700">
              {structuredBullets.map((b, i) => (
                <li key={i} className="flex gap-3">
                  <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
                  <span className="leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* NEXT STEPS */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-teal-700">
              {t("steps.eyebrow")}
            </p>
            <h2 className="mt-3 font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
              {t("steps.title")}
            </h2>
            <p className="mt-3 text-base text-slate-700 font-medium">
              {t("steps.subtitle")}
            </p>
            <ul className="mt-5 space-y-3 text-sm text-slate-700">
              {stepsBullets.map((b, i) => (
                <li key={i} className="flex gap-3">
                  <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-teal-600" />
                  <span className="leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-teal-700">
              {t("organize.eyebrow")}
            </p>
            <h2 className="mt-3 font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
              {t("organize.title")}
            </h2>
            <p className="mt-3 text-base text-slate-700 font-medium">
              {t("organize.subtitle")}
            </p>
            <ul className="mt-5 space-y-3 text-sm text-slate-700">
              {organizeBullets.map((b, i) => (
                <li key={i} className="flex gap-3">
                  <Layers className="mt-0.5 h-4 w-4 shrink-0 text-teal-600" />
                  <span className="leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section id="newsletter" className="bg-slate-900 text-slate-100">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-6">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-teal-400">
              {t("newsletter.eyebrow")}
            </p>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white">
              {t("newsletter.title")}
            </h2>
            <p className="mt-4 text-lg text-slate-300">
              {t("newsletter.subtitle")}
            </p>
            <div className="mt-6 rounded-xl border border-slate-800 bg-slate-950/60 p-5">
              <img
                src="/images/building.jpg"
                alt="Chasquifund"
                className="h-40 w-full rounded-md object-cover"
                loading="lazy"
              />
            </div>
          </div>
          <div className="lg:col-span-6 flex justify-center">
            <NewsletterForm />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="bg-white">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-teal-700">
            {t("faq.eyebrow")}
          </p>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
            {t("faq.title")}
          </h2>
          <p className="mt-3 text-base text-slate-700">{t("faq.subtitle")}</p>

          <Accordion
            type="single"
            collapsible
            className="mt-10 divide-y divide-slate-200 border-y border-slate-200"
          >
            {faqItems.map((item, i) => (
              <AccordionItem key={i} value={`item-${i + 1}`} className="border-0">
                <AccordionTrigger
                  data-testid={`faq-accordion-item-${i + 1}`}
                  className="text-left font-heading text-base sm:text-lg font-semibold text-slate-900"
                >
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm sm:text-base text-slate-700 leading-relaxed">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </main>
  );
}
